import React, { useEffect, useState } from 'react';
import { Newspaper, Loader2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface NewsItem {
  symbol: string | null;
  publisher: string;
  title: string;
  url: string;
  publishedDate: string;
}

// Compact home-page widget showing recent market news + IPO placeholders
const LIMIT = 12;

export const MarketNewsWidget: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const base = (import.meta.env.VITE_APP_API_URL as string) || 'http://localhost:5000';
        const endpoint = '/api/news/general-latest';
        const response = await fetch(`${base}${endpoint}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setNews(data.slice(0, LIMIT));
          }
        }
      } catch (err) {
        console.error('Failed fetching home news widget', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const timeAgo = (publishedDate: string) => {
    const now = new Date().getTime();
    const diff = now - new Date(publishedDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  return (
    <div className="w-full">
      {/* Market News (IPO sections temporarily removed) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-lg flex items-center gap-2"><Newspaper className="w-5 h-5 text-blue-500"/> Market News <span className="text-theme-muted text-sm">›</span></h3>
          <Link to="/news" className="text-sm text-blue-500 hover:text-blue-400 inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="rounded-lg border border-theme-tertiary bg-theme-secondary p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500"/></div>
          ) : (
            <ul className="divide-y divide-theme-tertiary">
              {news.map((n,i) => (
                <li key={i} className="py-2 flex items-start">
                  <span className="w-10 shrink-0 text-xs text-theme-muted">{timeAgo(n.publishedDate)}</span>
                  <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-500 leading-tight flex-1 pr-2">
                    {n.title}
                  </a>
                  <span className="hidden md:inline text-xs text-theme-muted">{n.publisher}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketNewsWidget;
